/**
 * The typed REST client — frontend.md §1.5 (M4 loopback token), §3.1, §4.2, §4.3;
 * conventions.md §2.3 (snake↔camel boundary), §6 (embedding-free wire rule).
 *
 * This module is the single chokepoint for every backend call. It does, on every request:
 *
 *  1. **Loopback token (M4).** Attaches `Authorization: Bearer <token>` — the opaque token
 *     the Tauri shell provisions into `window.__ALETHEIA__.loopbackToken` at startup. No
 *     surface ever sees the token; it never appears in a log line (conventions.md §4).
 *  2. **Correlation id.** Mints a UUID-v7-ish `queryId` per request and sends it as the
 *     `X-Query-Id` header so a frontend log line joins the backend's log for the same
 *     request (conventions.md §4 / frontend.md §4.3). The backend echoes it in
 *     `ResponseMeta.query_id`.
 *  3. **snake↔camel boundary mapping (conventions.md §2.3).** Request bodies go out
 *     camel→snake; responses come back snake→camel. The mapping lives ONLY here +
 *     `mappers.ts`.
 *  4. **The embedding-free guard (conventions.md §6).** Every inbound payload is walked by
 *     `assertNoEmbedding` — a raw `embedding`/`vector`/`centroid` field logs at ERROR and
 *     throws.
 *  5. **Two unwrap paths.** Tool routes (`/api/v1/<tool>`) return `Envelope<T>`; the
 *     frontend-supporting endpoints (`health/*`, `system/*`, ...) return their payload
 *     BARE. `callTool` unwraps the envelope; `callEndpoint` takes the body as-is.
 *  6. **`ErrorEnvelope` normalization (frontend.md §3.1).** Every failure path — a failed
 *     envelope, a non-2xx bare endpoint, a network error, a parse error — is normalized to
 *     one `ErrorEnvelope` with an explicit `category`, wrapped in `AletheiaApiError`, and
 *     thrown. `@tanstack/query` catches it; the error-handling layer branches on
 *     `.envelope.category`.
 *
 * The backend owns retry (conventions.md §7) — this client NEVER retries. A `retryable`
 * error is surfaced to the user, who re-issues on an explicit action.
 */

import type { Envelope, ErrorEnvelope, ErrorCategory } from '@/types';
import { AletheiaApiError } from '@/types';
import { connectivity } from '@/providers/connectivity';
import { assertNoEmbedding, keysToCamel, keysToSnake } from './mappers';
import { getLogger } from './logger';

const log = getLogger('api.client');

// ---------------------------------------------------------------------------
// Platform seam — the Tauri shell injects the loopback token + the API base URL
// ---------------------------------------------------------------------------

/** The boot-time globals the Tauri shell provisions into the webview (M4). */
interface AletheiaGlobals {
  /** The opaque loopback bearer token (M4). Provisioned at startup; never logged. */
  loopbackToken?: string;
  /** The backend base URL — defaults to the dev proxy origin when absent. */
  apiBaseUrl?: string;
}

declare global {
  interface Window {
    __ALETHEIA__?: AletheiaGlobals;
  }
}

/** Read the injected globals; empty object in a plain browser dev session (proxy handles it). */
function platformGlobals(): AletheiaGlobals {
  if (typeof window === 'undefined') return {};
  return window.__ALETHEIA__ ?? {};
}

/**
 * Build-time env fallback (cloud SWA deploy, 2026-05-17).
 *
 * The Tauri shell injects `window.__ALETHEIA__` at startup with the loopback token + the
 * backend URL — that path is for the local desktop build. The cloud build has no shell and
 * no `mcp.token` file, so we read the same values from Vite env vars that the SWA GitHub
 * Actions workflow stamps in at build time via repo secrets. Fallback order:
 *
 *   1. `window.__ALETHEIA__.X`              (Tauri shell injection, runtime)
 *   2. `import.meta.env.VITE_ALETHEIA_X`    (SWA build-time injection)
 *   3. empty / Vite dev-proxy default        (local `npm run dev` against local backend)
 *
 * Vite replaces `import.meta.env.VITE_*` references with string literals at build time, so
 * the secret is bound into the bundle. That's load-bearing: the SWA Free tier has no
 * server-side env layer to read from at request time, only a build-time substitution.
 * (If we later move to App Service or add a serverless front like SWA APIs, prefer a
 * request-time fetch over a build-time literal.)
 */
function viteEnvVar(name: string): string | undefined {
  // `import.meta.env` is a Vite construct; both Vite and TS know about it. Wrapped in
  // a defensive `?.` chain so a bundler other than Vite (or a `vitest` boot path) doesn't
  // null-pointer if it isn't populated.
  const env = import.meta.env as Record<string, string | undefined> | undefined;
  const raw = env?.[name];
  if (raw !== undefined && raw !== '') return raw;
  // 2026-05-19 — legacy fallback. The Aletheia rebrand renamed VITE_ALETHEIA_*
  // → VITE_ALETHEIA_*. If the SWA build hasn't been re-run with new repo
  // secret names yet (or a local .env file still has the old names), fall
  // back to the legacy name so the rename doesn't break the dev/cloud loop.
  if (name.startsWith('VITE_ALETHEIA_')) {
    const legacy = name.replace('VITE_ALETHEIA_', 'VITE_ALETHEIA_');
    const fallback = env?.[legacy];
    if (fallback !== undefined && fallback !== '') return fallback;
  }
  return undefined;
}

/** The API base URL — Tauri-injected → Vite env → same-origin (SWA proxy + dev proxy).
 *
 * Same-origin default is load-bearing for the cloud SWA deploy: the SWA serves the SPA
 * and a `/api/{*path}` Function proxy on the same origin, so an empty base URL hits the
 * proxy without CORS. The proxy server-side-injects the bearer token from the SWA's
 * App Settings (`ALETHEIA_BACKEND_TOKEN`) — the bundle never sees it. The Tauri-injected
 * override is still respected for the desktop build (which talks to the backend directly).
 */
function apiBaseUrl(): string {
  return platformGlobals().apiBaseUrl ?? viteEnvVar('VITE_ALETHEIA_BACKEND_URL') ?? '';
}

/**
 * A lightweight, non-throwing reachability probe for the connectivity prober
 * (`providers/connectivity.ts`). It hits the same lightweight status endpoint the status bar
 * polls — but as a raw fetch that bypasses the paused `@tanstack/query` layer, with a short
 * timeout, returning a bare boolean. It never toasts, never throws, and is the signal that
 * flips the app back online the moment the backend returns.
 */
export async function pingBackend(timeoutMs = 4000): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const token = platformGlobals().loopbackToken ?? viteEnvVar('VITE_ALETHEIA_API_TOKEN');
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${apiBaseUrl()}/api/v1/system/status`, {
      method: 'GET',
      headers,
      credentials: 'omit',
      signal: controller.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// Wire the reconnect prober once, at module load — the connectivity manager owns the cadence.
connectivity.configureProbe(pingBackend);

// ---------------------------------------------------------------------------
// Attachments — binary upload/download (Attachments & media track)
// ---------------------------------------------------------------------------
// These bypass the JSON `callEndpoint`/`keysToCamel` path because they move RAW BYTES
// (multipart up, blob down). The note body stores a logical `attachment:<id>` reference; the
// editor resolves it to an authed object URL at render time (so the bearer rides on a fetch,
// not an `<img src>`, and the canonical body stays portable + byte-stable).

function authHeaders(): Record<string, string> {
  const token = platformGlobals().loopbackToken ?? viteEnvVar('VITE_ALETHEIA_API_TOKEN');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Metadata returned by the upload endpoint — the `attachmentId` becomes the body reference. */
export interface UploadedAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  byteSize: number;
  contentHash: string;
}

/** Upload bytes as an attachment of a note. Returns its id; the body reference is `attachment:<id>`. */
export async function uploadAttachment(
  noteId: string,
  file: File | Blob,
  filename?: string,
): Promise<UploadedAttachment> {
  const form = new FormData();
  const name = filename ?? (file instanceof File ? file.name : 'attachment');
  form.append('file', file, name);
  const res = await fetch(`${apiBaseUrl()}/api/v1/notes/${noteId}/attachments`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
    credentials: 'omit',
  });
  if (!res.ok) throw new Error(`attachment upload failed (${res.status})`);
  const json = (await res.json()) as { data?: Record<string, unknown> };
  const d = json.data ?? {};
  return {
    attachmentId: String(d.attachment_id ?? d.attachmentId ?? ''),
    filename: String(d.filename ?? name),
    mimeType: String(d.mime_type ?? d.mimeType ?? 'application/octet-stream'),
    byteSize: Number(d.byte_size ?? d.byteSize ?? 0),
    contentHash: String(d.content_hash ?? d.contentHash ?? ''),
  };
}

/** id → object-URL cache so each attachment blob is fetched at most once per session. */
const _attachmentUrlCache = new Map<string, Promise<string>>();

/** Resolve `attachment:<id>` to a displayable object URL (authed fetch → blob → object URL). */
export function resolveAttachmentUrl(attachmentId: string): Promise<string> {
  let pending = _attachmentUrlCache.get(attachmentId);
  if (pending === undefined) {
    pending = (async () => {
      const res = await fetch(`${apiBaseUrl()}/api/v1/attachments/${attachmentId}`, {
        headers: authHeaders(),
        credentials: 'omit',
      });
      if (!res.ok) throw new Error(`attachment fetch failed (${res.status})`);
      return URL.createObjectURL(await res.blob());
    })();
    // Drop a rejected fetch from the cache so a later render can retry (transient backend blips).
    pending.catch(() => _attachmentUrlCache.delete(attachmentId));
    _attachmentUrlCache.set(attachmentId, pending);
  }
  return pending;
}

// ---------------------------------------------------------------------------
// Correlation ids — a per-request id, k-sortable, sent as X-Query-Id
// ---------------------------------------------------------------------------

/**
 * Mint a per-request correlation id. Uses `crypto.randomUUID()` (available in the Tauri
 * webview and every modern browser); the value is opaque to the backend, which accepts a
 * client-supplied `X-Query-Id` and threads it through every log line (conventions.md §4).
 */
function newQueryId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback — should not be hit in the target runtime; keeps the type total.
  return `qid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Error normalization → ErrorEnvelope (frontend.md §3.1)
// ---------------------------------------------------------------------------

/**
 * The stable-code → category map — the frontend's local copy of `api/errors.py`'s
 * stable-code table's category column (the wire `ApiErrorBody` does not carry `category`;
 * frontend.md §3.1 / §4.2 needs it explicit so the three UI handling paths can branch).
 * An unknown code defaults to `system` — a bug/invariant surface, the loudest path.
 */
const STABLE_CODE_CATEGORY: Record<string, ErrorCategory> = {
  unauthorized: 'user',
  forbidden_origin: 'user',
  not_found: 'user',
  note_deleted: 'user',
  validation_error: 'user',
  owner_not_configured: 'user',
  dependency_unavailable: 'external',
  internal_error: 'system',
};

/** Map a stable error code to its PRD §13.3 category — unknown codes are `system`. */
export function categoryForCode(code: string): ErrorCategory {
  return STABLE_CODE_CATEGORY[code] ?? 'system';
}

/** Build an `ErrorEnvelope` from a backend `ApiErrorBody` (the failed-envelope path). */
function envelopeFromApiError(
  body: { code: string; message: string; retryable: boolean; details: Record<string, unknown> | null },
  queryId: string | undefined,
): ErrorEnvelope {
  return {
    category: categoryForCode(body.code),
    code: body.code,
    message: body.message,
    retryable: body.retryable,
    context: body.details ?? undefined,
    queryId,
  };
}

/** Build an `ErrorEnvelope` for a transport-level failure (network down, parse error). */
function envelopeFromTransport(
  code: string,
  message: string,
  category: ErrorCategory,
  queryId: string | undefined,
  context?: Record<string, unknown>,
): ErrorEnvelope {
  return { category, code, message, retryable: category === 'external', context, queryId };
}

// ---------------------------------------------------------------------------
// The request core
// ---------------------------------------------------------------------------

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RawRequest {
  method: HttpMethod;
  /** Path under the API host, e.g. `/api/v1/search_hybrid` or `/api/v1/health/soft_deleted`. */
  path: string;
  /** Camel-cased body — mapped to snake_case before send. Omitted for GET. */
  body?: unknown;
  /** Query-string params — appended raw (already snake_case keys, primitive values). An array
   *  value is appended as repeated params (`?tag=a&tag=b`) for endpoints with list filters. */
  query?: Record<string, string | number | boolean | string[] | undefined>;
}

/** Append a query object to a path, dropping `undefined` values. Array values append one
 *  repeated param per element (`?k=a&k=b`); empty arrays append nothing. */
function withQuery(path: string, query: RawRequest['query']): string {
  if (!query) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.set(k, String(v));
    }
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

/** Header carrying the end-user identity token (Clerk session JWT). Must match the backend's
 *  `USER_SESSION_HEADER` (api/transport/middleware.py) and be forwarded by the SWA proxy. */
export const USER_SESSION_HEADER = 'X-Aletheia-Session';

/** Returns the current end-user session token (or null when signed out / no provider). */
type SessionTokenGetter = () => Promise<string | null | undefined>;
let _sessionTokenGetter: SessionTokenGetter | null = null;

/**
 * Register (or clear, with `null`) the getter the API client calls to attach the end-user
 * identity token on every request. A Clerk-aware component wires this to Clerk's `getToken()`
 * once, at mount; the desktop / single-owner build never registers one, so requests stay on
 * the owner-workspace path. Idempotent — last registration wins.
 */
export function setSessionTokenGetter(getter: SessionTokenGetter | null): void {
  _sessionTokenGetter = getter;
}

/**
 * Issue one HTTP request and return the parsed, camel-cased, embedding-checked JSON body.
 * Throws `AletheiaApiError` on any transport-level failure. Envelope-vs-bare unwrapping
 * is the caller's job (`callTool` / `callEndpoint`).
 */
async function rawFetch(req: RawRequest): Promise<{ json: unknown; queryId: string }> {
  const queryId = newQueryId();
  // Token source:
  // - Local Tauri shell: window.__ALETHEIA__.loopbackToken (injected at boot)
  // - Local dev (npm run dev): VITE_ALETHEIA_API_TOKEN if you want to test against an
  //   external backend; otherwise the Vite proxy handles same-origin to localhost:8765.
  // - Cloud SWA deploy: **undefined** here. The same-origin `/api/{*path}` SWA Function
  //   proxy injects the bearer header server-side from its App Settings — the bundle MUST
  //   NOT carry the token (extractable from the JS source by any visitor). VITE_ALETHEIA_API_TOKEN
  //   is intentionally unset on the cloud build to enforce that.
  const token = platformGlobals().loopbackToken ?? viteEnvVar('VITE_ALETHEIA_API_TOKEN');
  const headers: Record<string, string> = {
    'X-Query-Id': queryId,
    Accept: 'application/json',
  };
  if (req.body !== undefined) headers['Content-Type'] = 'application/json';
  // M4: the loopback token on every call. Never logged (conventions.md §4).
  if (token) headers['Authorization'] = `Bearer ${token}`;
  else log.warn('no_loopback_token', { path: req.path }, queryId);

  // Multi-tenancy: attach the end-user identity (Clerk session JWT) so the backend resolves
  // THIS user's workspace. Distinct from the Authorization bearer, which is the transport
  // credential (the SWA proxy injects a fixed system bearer server-side). Inert until a
  // Clerk-aware component registers a getter via `setSessionTokenGetter`; on the desktop /
  // single-owner path no getter is registered and the request acts in the owner workspace.
  if (_sessionTokenGetter) {
    try {
      const session = await _sessionTokenGetter();
      if (session) headers[USER_SESSION_HEADER] = session;
    } catch {
      // A token fetch failure must not break the request — it proceeds without a user
      // identity (the backend then treats it as the owner/system path, and any per-user
      // route the user actually needs will 401, surfacing the auth problem cleanly).
      log.warn('session_token_unavailable', { path: req.path }, queryId);
    }
  }

  const url = apiBaseUrl() + withQuery(req.path, req.query);
  const started = performance.now();

  let response: Response;
  try {
    response = await fetch(url, {
      method: req.method,
      headers,
      body: req.body !== undefined ? JSON.stringify(keysToSnake(req.body)) : undefined,
      // Same-origin in dev (proxy) / explicit loopback origin in Tauri — never cross-site.
      credentials: 'omit',
    });
  } catch (cause) {
    // A thrown fetch is a transport failure — the backend is unreachable. External category.
    // Tell the connectivity manager so the app pauses its queries (serving the local cache)
    // and the bottom-left indicator flips to "Web Services Unreachable" instead of the UI
    // erupting in error toasts/pages.
    connectivity.markUnreachable();
    log.error('transport_error', { path: req.path, error: String(cause) }, queryId);
    throw new AletheiaApiError(
      envelopeFromTransport(
        'dependency_unavailable',
        'The Aletheia backend is unreachable. It may still be starting up.',
        'external',
        queryId,
        { path: req.path },
      ),
    );
  }
  // The backend answered (a 4xx/5xx still means it's reachable) — clear any unreachable state
  // so paused queries resume.
  connectivity.markReachable();

  let parsed: unknown;
  const text = await response.text();
  try {
    parsed = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    log.error('parse_error', { path: req.path, status: response.status }, queryId);
    throw new AletheiaApiError(
      envelopeFromTransport(
        'internal_error',
        'The backend returned a response that could not be parsed.',
        'system',
        queryId,
        { path: req.path, status: response.status },
      ),
    );
  }

  // The embedding-free guard (conventions.md §6) — runs on EVERY inbound payload, before
  // mapping, before any unwrap. A raw vector field throws here.
  assertNoEmbedding(parsed);

  log.debug(
    'request_complete',
    { path: req.path, status: response.status, elapsedMs: Math.round(performance.now() - started) },
    queryId,
  );

  // A non-2xx response is ALWAYS an error — but the body shape varies:
  //   - the standard backend `ErrorEnvelope` (`{ok: false, error: {...}}`) — fall through
  //     to the public callers' (`callTool` / `callEndpoint`) existing envelope-error
  //     branches so they can map `error.code` to a category;
  //   - any non-envelope body (FastAPI's default `{detail: "Not Found"}` 404, a raw 500
  //     HTML page, an empty body) — throw HERE, mapping status → ErrorEnvelope. Without
  //     this, the bare body would tunnel through `keysToCamel` and be cast to the
  //     caller's expected payload type, silently returning a malformed object (e.g.
  //     `useNotebookChildrenQuery` saw `{detail: "..."}` as `NotebookChildren` and
  //     `?.notebooks ?? []` rendered "No notebooks yet" instead of an error).
  if (!response.ok) {
    const isStandardEnvelope =
      parsed !== null
      && typeof parsed === 'object'
      && 'ok' in parsed
      && (parsed as { ok?: unknown }).ok === false;
    if (!isStandardEnvelope) {
      throw new AletheiaApiError(httpStatusToEnvelope(response.status, req.path, queryId));
    }
  }

  return { json: keysToCamel(parsed), queryId };
}

/** Normalize a bare non-2xx HTTP status into an `ErrorEnvelope` (no JSON body case). */
function httpStatusToEnvelope(status: number, path: string, queryId: string): ErrorEnvelope {
  if (status === 401 || status === 403) {
    return envelopeFromTransport(
      'unauthorized',
      'The request was not authorized — the loopback token may be missing or stale.',
      'user',
      queryId,
      { path, status },
    );
  }
  if (status === 404) {
    return envelopeFromTransport('not_found', 'The requested resource was not found.', 'user', queryId, {
      path,
      status,
    });
  }
  if (status >= 500) {
    return envelopeFromTransport(
      'internal_error',
      'The backend reported an internal error.',
      'system',
      queryId,
      { path, status },
    );
  }
  return envelopeFromTransport('internal_error', `Unexpected HTTP ${status}.`, 'system', queryId, {
    path,
    status,
  });
}

// ---------------------------------------------------------------------------
// The two public call paths — tool routes (enveloped) vs frontend endpoints (bare)
// ---------------------------------------------------------------------------

/** The success result of a tool call — the typed payload + the response meta (pagination!). */
export interface ToolResult<T> {
  data: T;
  meta: Envelope<unknown>['meta'];
}

/**
 * Call a `/api/v1/<tool>` route (the 30-tool MCP/REST surface). These return `Envelope<T>`
 * — this unwraps it: on `ok` it returns `{data, meta}` (the caller needs `meta.nextCursor`
 * for pagination); on `!ok` it throws `AletheiaApiError` built from `envelope.error`.
 *
 * All tool routes are POST (mcp-api.md §4.4 — name-identical, no REST-idiomatic verbs).
 */
export async function callTool<T>(tool: string, body: unknown = {}): Promise<ToolResult<T>> {
  const { json, queryId } = await rawFetch({
    method: 'POST',
    path: `/api/v1/${tool}`,
    body,
  });
  const envelope = json as Envelope<T>;
  if (!envelope || typeof envelope !== 'object' || !('ok' in envelope)) {
    throw new AletheiaApiError(
      envelopeFromTransport(
        'internal_error',
        'The backend returned a non-envelope response from a tool route.',
        'system',
        queryId,
        { tool },
      ),
    );
  }
  if (!envelope.ok) {
    const err = envelope.error;
    throw new AletheiaApiError(
      err
        ? envelopeFromApiError(err, envelope.meta?.queryId ?? queryId)
        : envelopeFromTransport('internal_error', 'Tool failed with no error body.', 'system', queryId, {
            tool,
          }),
    );
  }
  return { data: envelope.data as T, meta: envelope.meta };
}

/**
 * Call a frontend-supporting REST endpoint (`health/*`, `system/*`, `sources/*`,
 * `settings`, `saved_searches`, the maintenance actions). These are REST-only — NOT MCP
 * tools — and return their payload BARE (not enveloped), so this returns the mapped body
 * directly. A non-2xx with a JSON error body is normalized via the wire `ApiErrorBody`
 * shape FastAPI produces for `raise validation_error(...)` etc.
 */
export async function callEndpoint<T>(
  method: HttpMethod,
  path: string,
  options: { body?: unknown; query?: RawRequest['query'] } = {},
): Promise<T> {
  const { json, queryId } = await rawFetch({
    method,
    path: path.startsWith('/') ? path : `/api/v1/${path}`,
    body: options.body,
    query: options.query,
  });
  // FastAPI renders a raised `ApiError` as an envelope-shaped body on these routes too
  // (the error path runs through `error_envelope`); a bare endpoint's *success* body is
  // not enveloped, but its *error* body is. Detect and unwrap the error case.
  if (json && typeof json === 'object' && 'ok' in json && (json as Envelope<T>).ok === false) {
    const err = (json as Envelope<T>).error;
    throw new AletheiaApiError(
      err
        ? envelopeFromApiError(err, queryId)
        : envelopeFromTransport('internal_error', 'Endpoint failed with no error body.', 'system', queryId, {
            path,
          }),
    );
  }
  return json as T;
}
